/**
 * Advanced Search Utilities
 * Provides case-insensitive, multi-word, and fuzzy matching search capabilities
 */

/**
 * Calculate Levenshtein distance between two strings
 * Used for fuzzy matching to handle typos
 */
function levenshteinDistance(str1, str2) {
  const matrix = [];
  const len1 = str1.length;
  const len2 = str2.length;

  // Initialize matrix
  for (let i = 0; i <= len2; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= len1; j++) {
    matrix[0][j] = j;
  }

  // Fill matrix
  for (let i = 1; i <= len2; i++) {
    for (let j = 1; j <= len1; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j] + 1 // deletion
        );
      }
    }
  }

  return matrix[len2][len1];
}

/**
 * Calculate similarity score between two strings (0-1, where 1 is identical)
 */
function calculateSimilarity(str1, str2) {
  const maxLength = Math.max(str1.length, str2.length);
  if (maxLength === 0) return 1;

  const distance = levenshteinDistance(str1.toLowerCase(), str2.toLowerCase());
  return 1 - distance / maxLength;
}

/**
 * Generate word variations for better matching
 * @param {string} word - The word to generate variations for
 * @returns {Array} - Array of word variations
 */
function generateWordVariations(word) {
  const variations = new Set([word]);

  // Add original word
  variations.add(word);

  // Add without last character (tirtira -> tirtir)
  if (word.length > 3) {
    variations.add(word.slice(0, -1));
    // Add without last 2 characters for longer words
    if (word.length > 5) {
      variations.add(word.slice(0, -2));
    }
  }

  // Add with extra character at end (tirtir -> tirtira)
  const commonEndings = ["a", "e", "i", "o", "u", "s", "r", "n", "h", "y"];
  commonEndings.forEach((ending) => {
    variations.add(word + ending);
  });

  // Add without first character
  if (word.length > 3) {
    variations.add(word.slice(1));
    // Add without first 2 characters for longer words
    if (word.length > 5) {
      variations.add(word.slice(2));
    }
  }

  // Add with extra character at start
  const commonStarts = ["a", "e", "i", "o", "u", "h"];
  commonStarts.forEach((start) => {
    variations.add(start + word);
  });

  // Add common vowel substitutions
  const vowelSubs = {
    a: ["e", "i", "o", "u"],
    e: ["a", "i", "o", "u"],
    i: ["a", "e", "o", "u"],
    o: ["a", "e", "i", "u"],
    u: ["a", "e", "i", "o"],
  };

  for (let i = 0; i < word.length; i++) {
    const char = word[i];
    if (vowelSubs[char]) {
      vowelSubs[char].forEach((vowel) => {
        const newWord = word.slice(0, i) + vowel + word.slice(i + 1);
        variations.add(newWord);
      });
    }
  }

  return Array.from(variations);
}

/**
 * Check for concatenated word matches
 * @param {string} searchTerm - The search term
 * @param {string} text - The text to search in
 * @returns {boolean} - Whether there's a concatenated match
 */
function checkConcatenatedMatch(searchTerm, text) {
  const normalizedSearch = searchTerm.toLowerCase().trim();
  const normalizedText = text.toLowerCase().trim();

  // Check if search term is a concatenation of text words
  const textWords = normalizedText.split(/\s+/);
  const searchWords = normalizedSearch.split(/\s+/);

  // Check if any search word is a concatenation of text words
  for (const searchWord of searchWords) {
    for (let i = 0; i < textWords.length - 1; i++) {
      const concatenated = textWords[i] + textWords[i + 1];
      if (searchWord === concatenated || concatenated.includes(searchWord)) {
        return true;
      }
    }
  }

  // Check if any text word is a concatenation of search words
  for (const textWord of textWords) {
    for (let i = 0; i < searchWords.length - 1; i++) {
      const concatenated = searchWords[i] + searchWords[i + 1];
      if (textWord === concatenated || textWord.includes(concatenated)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Check for word variation matches
 * @param {string} searchTerm - The search term
 * @param {string} text - The text to search in
 * @returns {boolean} - Whether there's a word variation match
 */
function checkWordVariationMatch(searchTerm, text) {
  const normalizedSearch = searchTerm.toLowerCase().trim();
  const normalizedText = text.toLowerCase().trim();

  const searchWords = normalizedSearch.split(/\s+/);
  const textWords = normalizedText.split(/\s+/);

  for (const searchWord of searchWords) {
    for (const textWord of textWords) {
      const searchVariations = generateWordVariations(searchWord);
      const textVariations = generateWordVariations(textWord);

      for (const searchVar of searchVariations) {
        for (const textVar of textVariations) {
          if (
            searchVar === textVar ||
            searchVar.includes(textVar) ||
            textVar.includes(searchVar)
          ) {
            return true;
          }
        }
      }
    }
  }

  return false;
}

/**
 * Check if a search term matches a text with fuzzy matching
 * @param {string} searchTerm - The term to search for
 * @param {string} text - The text to search in
 * @param {number} threshold - Similarity threshold (0-1, default 0.6)
 * @returns {boolean} - Whether the term matches
 */
function fuzzyMatch(searchTerm, text, threshold = 0.6) {
  if (!searchTerm || !text) return false;

  const normalizedSearch = searchTerm.toLowerCase().trim();
  const normalizedText = text.toLowerCase().trim();

  // Exact match gets highest priority
  if (normalizedText.includes(normalizedSearch)) {
    return true;
  }

  // Check for concatenated word matches (e.g., "maskfit" matches "mask fit")
  if (checkConcatenatedMatch(normalizedSearch, normalizedText)) {
    return true;
  }

  // Check if any word in the search term matches any word in the text
  const searchWords = normalizedSearch.split(/\s+/);
  const textWords = normalizedText.split(/\s+/);

  for (const searchWord of searchWords) {
    for (const textWord of textWords) {
      // Direct substring match
      if (textWord.includes(searchWord) || searchWord.includes(textWord)) {
        return true;
      }

      // Check word variations (e.g., "tirtira" matches "tirtir")
      const searchVariations = generateWordVariations(searchWord);
      const textVariations = generateWordVariations(textWord);

      for (const searchVar of searchVariations) {
        for (const textVar of textVariations) {
          if (
            searchVar === textVar ||
            searchVar.includes(textVar) ||
            textVar.includes(searchVar)
          ) {
            return true;
          }
        }
      }

      // Fuzzy match for typos
      if (calculateSimilarity(searchWord, textWord) >= threshold) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Advanced search function that handles multiple search strategies
 * @param {string} searchTerm - The search term
 * @param {Array} products - Array of products to search through
 * @param {Object} options - Search options
 * @returns {Array} - Filtered and ranked products
 */
export function advancedSearch(products, searchTerm, options = {}) {
  const {
    threshold = 0.6,
    searchFields = ["name", "brand", "description", "keywords"],
    maxResults = 100,
  } = options;

  if (!searchTerm || !products || products.length === 0) {
    return products || [];
  }

  const normalizedSearch = searchTerm.toLowerCase().trim();
  const searchWords = normalizedSearch
    .split(/\s+/)
    .filter((word) => word.length > 0);

  if (searchWords.length === 0) {
    return products;
  }

  const scoredProducts = products.map((product) => {
    let score = 0;
    let exactMatches = 0;
    let partialMatches = 0;
    let fuzzyMatches = 0;

    // Search through specified fields
    searchFields.forEach((field) => {
      const fieldValue = product[field];
      if (!fieldValue) return;

      const normalizedFieldValue = fieldValue.toString().toLowerCase();

      // Check each search word against this field
      searchWords.forEach((searchWord) => {
        // Exact match (highest priority)
        if (normalizedFieldValue.includes(searchWord)) {
          score += 10;
          exactMatches++;
        }
        // Partial match (word boundary)
        else if (
          new RegExp(`\\b${searchWord}`, "i").test(normalizedFieldValue)
        ) {
          score += 7;
          partialMatches++;
        }
        // Concatenated word match (e.g., "maskfit" matches "mask fit")
        else if (checkConcatenatedMatch(searchWord, normalizedFieldValue)) {
          score += 8;
          partialMatches++;
        }
        // Word variation match (e.g., "tirtira" matches "tirtir")
        else if (checkWordVariationMatch(searchWord, normalizedFieldValue)) {
          score += 6;
          partialMatches++;
        }
        // Fuzzy match
        else if (fuzzyMatch(searchWord, normalizedFieldValue, threshold)) {
          score += 3;
          fuzzyMatches++;
        }
      });
    });

    // Bonus for products that match multiple search words
    if (exactMatches > 1) score += 5;
    if (partialMatches > 1) score += 3;
    if (fuzzyMatches > 1) score += 1;

    // Bonus for exact brand matches
    if (product.brand && fuzzyMatch(normalizedSearch, product.brand, 0.8)) {
      score += 15;
    }

    // Bonus for exact name matches
    if (product.name && fuzzyMatch(normalizedSearch, product.name, 0.8)) {
      score += 12;
    }

    return {
      ...product,
      _searchScore: score,
      _exactMatches: exactMatches,
      _partialMatches: partialMatches,
      _fuzzyMatches: fuzzyMatches,
    };
  });

  // Filter out products with no matches and sort by score
  const filteredProducts = scoredProducts
    .filter((product) => product._searchScore > 0)
    .sort((a, b) => b._searchScore - a._searchScore)
    .slice(0, maxResults);

  return filteredProducts;
}

/**
 * Generate search suggestions based on product data
 * @param {string} searchTerm - The current search term
 * @param {Array} products - Array of products
 * @param {number} maxSuggestions - Maximum number of suggestions
 * @returns {Array} - Array of suggestion objects
 */
export function generateSearchSuggestions(
  searchTerm,
  products,
  maxSuggestions = 10
) {
  if (!searchTerm || !products || products.length === 0) {
    return [];
  }

  const normalizedSearch = searchTerm.toLowerCase().trim();
  const suggestions = new Set();

  products.forEach((product) => {
    // Add product name suggestions
    if (product.name && product.name.toLowerCase().includes(normalizedSearch)) {
      suggestions.add(product.name);
    }

    // Add brand suggestions
    if (
      product.brand &&
      product.brand.toLowerCase().includes(normalizedSearch)
    ) {
      suggestions.add(product.brand);
    }

    // Add keyword suggestions
    if (product.keywords && Array.isArray(product.keywords)) {
      product.keywords.forEach((keyword) => {
        if (keyword.toLowerCase().includes(normalizedSearch)) {
          suggestions.add(keyword);
        }
      });
    }
  });

  return Array.from(suggestions)
    .slice(0, maxSuggestions)
    .map((suggestion) => ({
      text: suggestion,
      type: "suggestion",
    }));
}

/**
 * Parse search query to extract search terms and filters
 * @param {string} query - The search query
 * @returns {Object} - Parsed search terms and filters
 */
export function parseSearchQuery(query) {
  if (!query) return { searchTerms: [], filters: {} };

  const terms = query.toLowerCase().trim().split(/\s+/);
  const filters = {};
  const searchTerms = [];

  terms.forEach((term) => {
    // Check for filter patterns (e.g., "brand:nike", "price:100-200")
    if (term.includes(":")) {
      const [key, value] = term.split(":", 2);
      filters[key] = value;
    } else {
      searchTerms.push(term);
    }
  });

  return {
    searchTerms: searchTerms.join(" "),
    filters,
  };
}

/**
 * Highlight search terms in text
 * @param {string} text - The text to highlight
 * @param {string} searchTerm - The search term to highlight
 * @returns {string} - HTML string with highlighted terms
 */
export function highlightSearchTerms(text, searchTerm) {
  if (!text || !searchTerm) return text;

  const regex = new RegExp(
    `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi"
  );
  return text.replace(regex, "<mark>$1</mark>");
}

export default {
  advancedSearch,
  generateSearchSuggestions,
  parseSearchQuery,
  highlightSearchTerms,
  fuzzyMatch,
  calculateSimilarity,
};
